/** @jsxImportSource react */
import EmbedAndShare from '@react/data-hub/components/EmbedAndShare';
import {
  generateLineChartHeading,
  generateSummaryParagraph,
  getGrowthRateText,
} from '@react/data-hub/helpers';
import { useDatahub } from '@react/data-hub/hooks/useDatahub';
import Logo from '@react/shared/components/Logo';
import { ROOM_OPTIONS } from '@react/widgets/pricingTrend/constants';
import { formatMoney } from '@sly/core/helpers/numbers';
import { useRef } from 'react';

import NoData from './NoData';

export default function YearlyTrendsSection({
  children,
}: {
  children: React.ReactNode;
}) {
  const chartContainer = useRef(null);

  const snapshotContainer = useRef(null);

  const { searchInsights, metadata } = useDatahub();

  const roomType = metadata?.incoming_filters?.['room-type']
    ? metadata?.incoming_filters?.['room-type']
    : '';

  const roomTypeLabel =
    ROOM_OPTIONS.find((option) => option.value === roomType)?.label || '';

  const summaryParagraph = searchInsights
    ? generateSummaryParagraph(searchInsights)
    : '';
  const lineChartHeading = searchInsights
    ? generateLineChartHeading({
        ...searchInsights,
        roomType: roomTypeLabel,
      })
    : '';

  const {
    careType,
    name: entityName,
    cityAveragePricing,
    info,
  } = searchInsights || {};

  const { yearlyTrends } = info || {};

  const growthRateText = yearlyTrends
    ? getGrowthRateText(yearlyTrends)
    : yearlyTrends;

  const noData = !yearlyTrends;

  return (
    <>
      <div>
        <h4 className='font-medium'>
          How are <span className='lowercase'>{careType}</span> costs{' '}
          {entityName} this year?
        </h4>
        <p>{summaryParagraph}</p>
      </div>

      <div className='py-4' ref={chartContainer}>
        <div className='w-full rounded border border-slate-lighter-90'>
          <div className='w-full bg-slate-lighter-95 p-6'>
            <div className='font-medium'>{lineChartHeading}</div>
            {cityAveragePricing && (
              <div className='font-t-xl'>{formatMoney(cityAveragePricing)}</div>
            )}

            {growthRateText && (
              <div className='text-harvest-darker-20'>{growthRateText}</div>
            )}
          </div>

          <NoData noData={noData}>
            {children}
            <div ref={snapshotContainer} className='hidden border-b p-4'>
              <div className='flex w-full items-center justify-between '>
                <p className='font-medium text-viridian-base'>
                  See more on Seniorly
                </p>
                <Logo className='text-viridian-base' />
              </div>
            </div>
          </NoData>
        </div>
      </div>
      <div>
        <EmbedAndShare
          disabled={noData}
          chartContainer={chartContainer}
          snapshotContainer={snapshotContainer}
        />
      </div>
    </>
  );
}
